import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { GripVertical, X } from 'lucide-react';

const COLORS = {
  cream: { bg: '#F4EFDF', text: '#2D2A1E', clip: '#C9A84C' },
  mint: { bg: '#BEEBD6', text: '#123' , clip: '#3EB489' },
  gold: { bg: '#E9D79A', text: '#3a2f10', clip: '#C9A84C' },
  sage: { bg: '#A6C3B4', text: '#10241b', clip: '#2D4A3E' },
};

export const StickyNote = ({ note, onChange, onDelete, dragProps }) => {
  const [text, setText] = useState(note.text || '');
  const [editing, setEditing] = useState(false);
  const ref = useRef();
  const c = COLORS[note.color] || COLORS.mint;

  useEffect(() => { setText(note.text || ''); }, [note.text]);

  const onMove = (e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const mx = ((e.clientX - r.left) / r.width - 0.5) * 18;
    const my = ((e.clientY - r.top) / r.height - 0.5) * 18;
    ref.current.style.filter = `drop-shadow(${-mx}px ${8 - my}px 16px rgba(0,0,0,0.35))`;
  };
  const onLeave = () => { if (ref.current) ref.current.style.filter = 'drop-shadow(0px 10px 16px rgba(0,0,0,0.3))'; };

  const save = () => { setEditing(false); if (text !== note.text) onChange({ ...note, text }); };

  return (
    <motion.div
      data-testid={`sticky-note-${note.id}`}
      ref={ref}
      className="pp-paper group relative select-none"
      style={{ width: 176, minHeight: 168, background: c.bg, color: c.text, rotate: `${note.rotation || 0}deg`,
        borderRadius: 6, padding: '26px 14px 14px', filter: 'drop-shadow(0px 10px 16px rgba(0,0,0,0.3))' }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      initial={{ scale: 0.9, opacity: 0, y: -20 }}
      animate={{ scale: 1, opacity: 1, y: 0 }}
      whileHover={{ scale: 1.03, zIndex: 30 }}
      transition={{ type: 'spring', stiffness: 260, damping: 20 }}
    >
      {/* metallic clip */}
      <svg width="40" height="26" viewBox="0 0 40 26" className="absolute -top-3 left-1/2 -translate-x-1/2" style={{ filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.35))' }}>
        <defs>
          <linearGradient id={`clip-${note.id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f5f5f5" /><stop offset="45%" stopColor={c.clip} /><stop offset="100%" stopColor="#7a6a2f" />
          </linearGradient>
        </defs>
        <rect x="12" y="2" width="16" height="22" rx="7" fill="none" stroke={`url(#clip-${note.id})`} strokeWidth="4" />
      </svg>

      <div className="absolute top-1 left-1 opacity-0 group-hover:opacity-70 cursor-grab" {...(dragProps || {})}>
        <GripVertical size={14} />
      </div>
      <button data-testid={`sticky-delete-${note.id}`} onClick={() => onDelete(note.id)} className="absolute top-1 right-1 opacity-0 group-hover:opacity-70 hover:opacity-100"><X size={14} /></button>

      {editing ? (
        <textarea autoFocus value={text} onChange={(e) => setText(e.target.value)} onBlur={save}
          className="w-full h-full bg-transparent resize-none outline-none text-sm font-medium" style={{ color: c.text, minHeight: 120 }} />
      ) : (
        <div onClick={() => setEditing(true)} className="text-sm font-medium whitespace-pre-wrap cursor-text" style={{ minHeight: 120 }}>
          {text || 'Escribe una nota...'}
        </div>
      )}
      {/* corner curl */}
      <div className="absolute bottom-0 right-0 w-6 h-6" style={{ background: 'linear-gradient(135deg, transparent 50%, rgba(0,0,0,0.12) 50%)', borderBottomRightRadius: 6 }} />
    </motion.div>
  );
};
